import React from 'react';
import { useField, useFormikContext } from 'formik';
import Grid from '@mui/material/Grid';
import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FormControl } from '@mui/base/FormControl';
import { styled } from '@mui/material/styles';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloseIcon from '@mui/icons-material/Close';

// Räume, bei denen mindestens ein Foto zwingend angehängt werden muss
// (wird auch in warranty.js für die Validierung verwendet)
export const FOTO_PFLICHT_RAEUME = ['Badezimmer', 'Küche'];

const Reklamation = () => {
  const [raumField, raumMeta] = useField('raum');
  const [detailsField, detailsMeta] = useField('details');
  const [mangelField, , mangelhelpers] = useField('magelart'); // bewusst: "magelart"
  const [files, , filehelpers] = useField('files');
  const { values, errors } = useFormikContext();

  const fotoPflicht = FOTO_PFLICHT_RAEUME.includes(values.raum);

  const handleOptionChange = (event) => {
    mangelhelpers.setValue(event.target.value);
  };

  const handleFileChange = (event) => {
    const fileList = Array.from(event.currentTarget.files || []);
    const updatedFiles = values.files ? [...values.files, ...fileList] : fileList;
    filehelpers.setValue(updatedFiles);
  };

  const handleFileRemove = (index) => {
    const updated = [...values.files];
    updated.splice(index, 1);
    filehelpers.setValue(updated);
  };

  const RadioBox = ({ icon, label, sublabel, value }) => {
    const isChecked = mangelField.value === value;

    return (
      <Box
        onClick={() => mangelhelpers.setValue(value)}
        className={isChecked ? 'checked-box' : 'unchecked-box'}
        sx={{
          border: '1px solid',
          borderColor: isChecked ? '#3A8787' : '#E7EBF0',
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          height: '100%',
          cursor: 'pointer',
          boxShadow: 1,                 // Schatten immer
          '&:hover': { boxShadow: 2 }, // etwas stärker beim Hover
          backgroundColor: isChecked ? '#2D2F31' : '#fff',      // invertieren, wenn ausgewählt
          color: isChecked ? '#fff' : 'inherit',
          transition: 'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
        }}
      >
        <FormControlLabel
          control={
            <Radio
              value={value}
              checked={isChecked}
              onChange={handleOptionChange}
              sx={{ display: 'none' }} // ganze Box ist klickbar
            />
          }
          label={
            <>
              {/* Kopfzeile: auf Desktop links Text, rechts Haken; auf Mobil nur Text */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { md: 'center' },
                  justifyContent: 'space-between',
                  mb: { xs: 1, md: 2 },
                }}
              >
                {/* Icon + Überschrift */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {React.cloneElement(icon, {
                    sx: { fontSize: { xs: 24, md: 28 }, color: isChecked ? '#A9D2D2' : 'inherit' },
                  })}
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: 18, md: 28 },   // Desktop wie gehabt groß
                      lineHeight: 1.2,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Haken nur auf Desktop/Tablet, auf Mobil komplett weg */}
                <CheckRoundedIcon
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    fontSize: 60,
                    color: isChecked ? 'white' : '#F0F2F5',
                    backgroundColor: isChecked ? '#3A8787' : 'white',
                    border: `1px solid ${isChecked ? '#3A8787' : '#F0F2F5'}`,
                    borderRadius: '50%',
                    p: 1,
                  }}
                />
              </Box>

              {/* Sublabel: auf Mobil unter der Überschrift, auf Desktop darunter (wie vorher) */}
              <Typography sx={{ fontSize: { xs: 13, md: 14 }, opacity: isChecked ? 0.95 : 1 }}>
                {sublabel}
              </Typography>
            </>
          }
        />
      </Box>
    );
  };

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  return (
    <div>
      <h2>Reklamation</h2>
      
      {/* Auswahl-Karten */}
      <FormControl>
        <RadioGroup name="mangelart">
          <p class="mb-0">Wählen Sie bitte die Art der Reklamation aus:<span className="MuiFormLabel-asterisk">*</span></p>
          <Grid container spacing={2} mb={6} mt={1}>
            <Grid item xs={12} md={4}>
              <RadioBox
                icon={<VisibilityOutlinedIcon />}
                label="Optischer Mangel"
                sublabel="Bitte beachten: Fügen Sie ein Bild mit Meterstab im Bildausschnitt mit an"
                value="Optischer Mangel"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RadioBox
                icon={<VolumeUpOutlinedIcon />}
                label="Akustischer Mangel"
                sublabel="Bitte beachten: Fügen Sie eine Ton- oder Videoaufnahme mit an"
                value="Akustischer Mangel"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RadioBox
                icon={<SettingsOutlinedIcon />}
                label="Funktionaler Mangel"
                sublabel="Bitte beachten: Fügen Sie eine Bild- oder Videoaufnahme an"
                value="Funktionaler Mangel"
              />
            </Grid>
          </Grid>
        </RadioGroup>
      </FormControl>

      <h5>Details</h5>
      <Grid container spacing={2} mt={0} mb={4}>
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl {...raumField} variant="standard" required error={raumMeta.touched && Boolean(errors.raum)}>
                <label>Wo befindet sich der Mangel genau?</label>
                <Select className="wb-select" value={raumField.value} onChange={raumField.onChange} name="raum">
                  <MenuItem value="Diele/Flur">Diele/Flur</MenuItem>
                  <MenuItem value="Wohnzimmer">Wohnzimmer</MenuItem>
                  <MenuItem value="Essbereich">Essbereich</MenuItem>
                  <MenuItem value="Küche">Küche</MenuItem>
                  <MenuItem value="Schlafzimmer">Schlafzimmer</MenuItem>
                  <MenuItem value="Kinderzimmer">Kinderzimmer</MenuItem>
                  <MenuItem value="Badezimmer">Badezimmer</MenuItem>
                  <MenuItem value="Gäste-WC">Gäste-WC</MenuItem>
                  <MenuItem value="Abstellraum Whg.">Abstellraum Whg.</MenuItem>
                  <MenuItem value="Abstellraum Balkon">Abstellraum Balkon</MenuItem>
                  <MenuItem value="Abstellraum UG">Abstellraum UG</MenuItem>
                  <MenuItem value="Hobbyraum">Hobbyraum</MenuItem>
                  <MenuItem value="Balkon/Terrasse">Balkon/Terrasse</MenuItem>
                  <MenuItem value="(TG-) Stellplatz">(TG-) Stellplatz</MenuItem>
                  <MenuItem value="Carport">Carport</MenuItem>
                </Select>
                {raumMeta.touched && errors.raum && <Typography sx={{ color: 'red' }}>{errors.raum}</Typography>}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl className="wb-input-wrapper" required  error={detailsMeta.touched && Boolean(errors.details)}>
                <label>Detailbeschreibung</label>
                <BaseTextareaAutosize
                  {...detailsField}
                  minRows={5}
                  maxRows={5}
                  name="details"
                  aria-label="details"
                  style={{ width: '100%' }}
                />
                {detailsMeta.touched && errors.details && <Typography sx={{ color: 'red' }}>{errors.details}</Typography>}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <h5>
        Dateien{' '}
        {fotoPflicht
          ? <span className="MuiFormLabel-asterisk">*</span>
          : <small>(optional)</small>}
      </h5>
      {fotoPflicht && (
        <Typography sx={{ color: errors.files ? 'red' : '#8C939C', mb: 1 }}>
          Für den Bereich „{values.raum}“ ist mindestens ein Foto des Mangels erforderlich.
        </Typography>
      )}
      <FormControl>
        <Grid container spacing={2} my={0}>
          {files.value &&
            files.value.map((file, index) => (
              <Grid item xs={12} sm={6} md={4} key={`${file.name}-${index}`}>
                <Box p={3} sx={{ border: '1px solid #F0F2F5' }}>
                  <Typography pb={0.5} sx={{ wordBreak: 'break-word' }}>
                    {file.name}
                  </Typography>
                  <Typography pb={2} sx={{ color: '#8C939C' }}>
                    Größe: {(file.size / 1e6).toFixed(2)} MB
                  </Typography>
                  <Button onClick={() => handleFileRemove(index)} sx={{ color: '#3a8787', textTransform: 'unset', p: 0 }}>
                    <CloseIcon sx={{ color: '#3a8787', fontSize: 20 }} />
                    Entfernen
                  </Button>
                </Box>
              </Grid>
            ))}

          <Grid item xs={12} sm={6} md={4}>
            <Box p={3} sx={{ border: '1px solid #F0F2F5', height: '100%' }}>
              <Button component="label" sx={{ display: 'flex', flexDirection: 'column', height: '100%', textTransform: 'unset' }}>
                <AddCircleIcon sx={{ color: '#3a8787', fontSize: 40, mb: 2 }} />
                <Typography sx={{ color: '#3a8787' }}>Datei hochladen</Typography>
                <VisuallyHiddenInput type="file" multiple name="files" onChange={handleFileChange} />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </FormControl>
    </div>
  );
};

export default Reklamation;